"use client";

import React from "react";
import EditSection, { TextEditor } from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";
import { Input } from "rizzui";
import { Select } from "@/components/ui/select";
import { PiTrash, PiPlusBold, PiX, PiUploadSimple } from "react-icons/pi";

export default function EducaeetHeroEdit({
  content,
  onContentChange,
  allSections = [],
}: SectionContentProps & { allSections?: any[] }) {
  const updateContent = (key: string, value: any) => {
    onContentChange?.({ ...content, [key]: value });
  };

  const navItems = content.navItems || ["Home", "Courses", "About", "Contact"];

  return (
    <EditSection
      content={content}
      onContentChange={onContentChange || (() => {})}
      contentFields={
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-4">
            {/* LOGO MANAGER */}
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block">
                Logo
              </label>

              {/* Current Logo Preview */}
              {content.logo && (
                <div className="bg-white p-3 rounded-lg border border-gray-200 mb-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-700">Current Logo</p>
                    {content.logo.startsWith("/") || content.logo.startsWith("http") || content.logo.startsWith("data:image") ? (
                      <img src={content.logo} alt="Logo Preview" className="max-h-12 w-auto object-contain mt-1" />
                    ) : (
                      <p className="text-sm font-bold text-emerald-800 mt-0.5">{content.logo}</p>
                    )}
                  </div>
                  <button
                    onClick={() => updateContent("logo", "Instacks")}
                    className="p-1.5 rounded-md bg-red-50 text-red-500 hover:bg-red-100"
                    title="Remove Logo"
                  >
                    <PiX className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Text input for brand name */}
              <Input
                value={content.logo || ""}
                onChange={(e) => updateContent("logo", e.target.value)}
                placeholder="Brand Name (e.g. Instacks)"
                className="!rounded-lg mb-2"
              />

              {/* Upload button simulation */}
              <label className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-white border border-dashed border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 hover:border-blue-400 cursor-pointer transition-all">
                <PiUploadSimple className="w-4 h-4 text-gray-500" />
                <span>Upload Logo</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (uploadEvent) => {
                        updateContent("logo", uploadEvent.target?.result as string);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </label>
            </div>

            {/* COMPANY / BRAND NAME */}
            <div>
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5 block">
                Company Name
              </label>
              <Input
                value={content.companyName || content.logo || ""}
                onChange={(e) => updateContent("companyName", e.target.value)}
                placeholder="Brand Name"
                className="!rounded-lg"
              />
            </div>

            {/* ITEMISED NAVIGATION ITEMS */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Navigation Items
                </label>
                <button
                  onClick={() => {
                    const formatted = navItems.map((item: any) =>
                      typeof item === "string" ? { label: item, sectionId: item.toLowerCase() } : item
                    );
                    updateContent("navItems", [...formatted, { label: "New Link", sectionId: "hero" }]);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1"
                >
                  <PiPlusBold className="w-3 h-3" /> Add
                </button>
              </div>

              <div className="space-y-3">
                {navItems.map((item: any, idx: number) => {
                  const navItem = typeof item === "string"
                    ? { label: item, sectionId: item.toLowerCase() }
                    : item;

                  return (
                    <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 flex gap-2 relative shadow-xs">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                            Nav Item
                          </label>
                          <Input
                            value={navItem.label || ""}
                            onChange={(e) => {
                              const currentNav = [...navItems];
                              currentNav[idx] = typeof item === "string"
                                ? { label: e.target.value, sectionId: navItem.sectionId }
                                : { ...navItem, label: e.target.value };
                              updateContent("navItems", currentNav);
                            }}
                            className="!rounded-md text-xs"
                            placeholder="Work"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                            Section Link
                          </label>
                          <Select
                            value={navItem.sectionId || "hero"}
                            onChange={(val: any) => {
                              const v = val?.target?.value || val?.value || val;
                              const currentNav = [...navItems];
                              currentNav[idx] = typeof item === "string"
                                ? { label: navItem.label, sectionId: v }
                                : { ...navItem, sectionId: v };
                              updateContent("navItems", currentNav);
                            }}
                            options={[
                              ...(allSections || [])
                                .filter(s => s && s.type !== "header" && s.type !== "footer")
                                .map((sec, i) => ({
                                  label: `${i + 1}. ${sec.title || sec.type}`,
                                  value: sec.type
                                })),
                              { label: "Custom Link", value: "custom" }
                            ]}
                            className="w-full text-xs !rounded-md"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const currentNav = navItems.filter((_: any, i: number) => i !== idx);
                          updateContent("navItems", currentNav);
                        }}
                        className="self-center p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50"
                        title="Remove Nav Item"
                      >
                        <PiTrash className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* LOGIN / CTA BUTTON */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5 block">
                  Main CTA Text
                </label>
                <Input
                  value={content.ctaText || "Login"}
                  onChange={(e) => updateContent("ctaText", e.target.value)}
                  placeholder="Login"
                  className="!rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5 block">
                  Header CTA Text
                </label>
                <Input
                  value={content.headerCtaText || "Login"}
                  onChange={(e) => updateContent("headerCtaText", e.target.value)}
                  placeholder="Login"
                  className="!rounded-lg"
                />
              </div>
            </div>

            {/* FLOATING STATS BADGES */}
            <div className="space-y-4 pt-2 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <PiUploadSimple className="h-4 w-4 text-emerald-600" />
                <h4 className="font-bold text-gray-900 text-xs uppercase tracking-wide">Floating Stats Badges</h4>
              </div>

              {/* Stat 1 */}
              <div className="bg-white p-3 rounded-lg border border-gray-200 space-y-2">
                <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wide">Badge 1 (Bottom Left)</span>
                <div>
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Label</label>
                  <Input
                    value={content.stats?.[0]?.label || "Active Students"}
                    onChange={(e) => {
                      const currentStats = content.stats || [
                        { value: "15k+", label: "Active Students" },
                        { value: "10k+", label: "Online Video Courses" }
                      ];
                      const updated = [...currentStats];
                      updated[0] = { ...updated[0], label: e.target.value };
                      updateContent("stats", updated);
                    }}
                    className="!rounded-md text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Value</label>
                  <Input
                    value={content.stats?.[0]?.value || "15k+"}
                    onChange={(e) => {
                      const currentStats = content.stats || [
                        { value: "15k+", label: "Active Students" },
                        { value: "10k+", label: "Online Video Courses" }
                      ];
                      const updated = [...currentStats];
                      updated[0] = { ...updated[0], value: e.target.value };
                      updateContent("stats", updated);
                    }}
                    className="!rounded-md text-xs"
                  />
                </div>
              </div>

              {/* Stat 2 */}
              <div className="bg-white p-3 rounded-lg border border-gray-200 space-y-2">
                <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wide">Badge 2 (Middle Right)</span>
                <div>
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Label</label>
                  <Input
                    value={content.stats?.[1]?.label || "Online Video Courses"}
                    onChange={(e) => {
                      const currentStats = content.stats || [
                        { value: "15k+", label: "Active Students" },
                        { value: "10k+", label: "Online Video Courses" }
                      ];
                      const updated = [...currentStats];
                      updated[1] = { ...updated[1], label: e.target.value };
                      updateContent("stats", updated);
                    }}
                    className="!rounded-md text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Value</label>
                  <Input
                    value={content.stats?.[1]?.value || "10k+"}
                    onChange={(e) => {
                      const currentStats = content.stats || [
                        { value: "15k+", label: "Active Students" },
                        { value: "10k+", label: "Online Video Courses" }
                      ];
                      const updated = [...currentStats];
                      updated[1] = { ...updated[1], value: e.target.value };
                      updateContent("stats", updated);
                    }}
                    className="!rounded-md text-xs"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <TextEditor
              label="Subtitle"
              value={content.subtitle}
              onChange={(v) => updateContent("subtitle", v)}
              placeholder="Enter subtitle or description"
              multiline
            />
          </div>
        </div>
      }
    />
  );
}
